import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  PoButtonModule,
  PoFieldModule,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoModalModule,
  PoModalComponent,
  PoModalAction,
  PoSelectOption
} from '@po-ui/ng-components';

import { Opcao, RecursoRegra, RegraBase } from '../shared/regras-api.service';
import { RegrasListaBase } from '../shared/regras-lista.base';
import { RegrasSkeletonComponent, RegrasToolbarComponent } from '../shared/regras-toolbar';
import { PoComboListaVisivelDirective } from '../../../shared/po-combo-lista-visivel.directive';

/** Natureza financeira por perfil de retenção (tabela ZOK). */
export interface RegraNatureza extends RegraBase {
  codigo?: string;
  filial: string;
  irrf: string;
  pcc: string;
  inss: string;
  iss: string;
  natureza: string;
  tolerancia: number;
  ordem: string;
  dtIni: string;
  dtFim: string;
  vigencia?: string;
}

/**
 * Aba de natureza financeira por perfil de retenção (ZOK).
 *
 * O que esta tela cadastra não é uma comparação de valores, e sim de
 * PERCENTUAIS de retenção sobre o valor do serviço, com tolerância — daí o
 * campo Tolerância, que nasce em 0,10 ponto percentual e só muda por decisão
 * fiscal. É por isso que o IRRF aparece como faixa (1,0% / 1,5%) e não como
 * "tem ou não tem": as duas alíquotas levam a naturezas diferentes.
 */
@Component({
  selector: 'app-regras-natureza',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PoButtonModule,
    PoFieldModule,
    PoTableModule,
    PoModalModule,
    RegrasToolbarComponent,
    RegrasSkeletonComponent,
    PoComboListaVisivelDirective
  ],
  templateUrl: './natureza.html',
  styles: [`
    .regras-auditoria { color: var(--color-neutral-dark-70, #6e6e6e); }
    ::ng-deep .regras-form .po-row { margin-bottom: 1.5rem; }
    ::ng-deep .regras-form .po-row:last-child { margin-bottom: 0; }
  `]
})
export class RegrasNaturezaComponent extends RegrasListaBase<RegraNatureza> {
  @ViewChild('formModal', { static: true }) formModal!: PoModalComponent;

  protected readonly recurso: RecursoRegra = 'natureza';
  protected readonly rotulo = 'as naturezas por perfil de retenção';

  private readonly fb = inject(FormBuilder);

  editing: RegraNatureza | null = null;
  duplicando = false;

  readonly formGroup: FormGroup = this.fb.group({
    codigo: [''],
    filial: [''],
    irrf: ['', Validators.required],
    pcc: ['', Validators.required],
    inss: ['', Validators.required],
    iss: ['', Validators.required],
    natureza: ['', Validators.required],
    // 0,10 ponto percentual é a tolerância que o código aplica hoje.
    tolerancia: [0.10],
    ordem: ['', Validators.required],
    dtIni: [this.hojeIso()],
    dtFim: [''],
    ativo: ['1', Validators.required]
  });

  filiaisOptions: PoSelectOption[] = [];

  readonly irrfOptions: PoSelectOption[] = [
    { label: 'Sem retenção', value: '1' },
    { label: 'Alíquota de 1,0%', value: '2' },
    { label: 'Alíquota de 1,5%', value: '3' },
    { label: 'Indiferente', value: '4' }
  ];

  get modalTitle(): string {
    if (this.duplicando) { return 'Duplicar natureza'; }
    return this.editing ? 'Editar natureza' : 'Nova natureza';
  }

  private readonly simNao = [
    { value: '1', label: 'Sim', color: 'color-10' },
    { value: '2', label: 'Não', color: 'color-07' }
  ];

  readonly columns: Array<PoTableColumn> = [
    {
      property: 'codigo', label: 'Código', width: '110px', type: 'link',
      action: (_: string, row: RegraNatureza) => this.editar(row)
    },
    { property: 'filial', label: 'Filial', width: '130px' },
    {
      property: 'irrf', label: 'IRRF', width: '150px', type: 'label',
      labels: [
        { value: '1', label: 'Sem retenção', color: 'color-07' },
        { value: '2', label: '1,0%', color: 'color-08' },
        { value: '3', label: '1,5%', color: 'color-01' },
        { value: '4', label: 'Indiferente', color: 'color-09' }
      ]
    },
    { property: 'pcc', label: 'PIS/COFINS/CSLL', width: '170px', type: 'label', labels: this.simNao },
    { property: 'inss', label: 'INSS', width: '110px', type: 'label', labels: this.simNao },
    { property: 'iss', label: 'ISS', width: '110px', type: 'label', labels: this.simNao },
    { property: 'natureza', label: 'Natureza', width: '130px' },
    { property: 'tolerancia', label: 'Tolerância', width: '130px', type: 'number', format: '1.2-2' },
    { property: 'ordem', label: 'Ordem', width: '110px' },
    { property: 'vigencia', label: 'Vigência', width: '180px' },
    {
      property: 'ativo', label: 'Situação', width: '130px', type: 'label',
      labels: [
        { value: '1', label: 'Ativa', color: 'color-10' },
        { value: '2', label: 'Inativa', color: 'color-07' }
      ]
    }
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: 'Editar', icon: 'an an-pencil-simple', action: (row: RegraNatureza) => this.editar(row) },
    { label: 'Duplicar', icon: 'an an-copy', action: (row: RegraNatureza) => this.duplicar(row) },
    {
      label: 'Inativar', icon: 'an an-lock', type: 'danger',
      action: (row: RegraNatureza) => this.alterarSituacao(row, '2'),
      visible: (row: RegraNatureza) => row.ativo === '1'
    },
    {
      label: 'Reativar', icon: 'an an-lock-open',
      action: (row: RegraNatureza) => this.alterarSituacao(row, '1'),
      visible: (row: RegraNatureza) => row.ativo === '2'
    }
  ];

  readonly modalPrimaryAction: PoModalAction = { action: () => this.salvar(), label: 'Salvar' };
  readonly modalSecondaryAction: PoModalAction = { action: () => this.formModal.close(), label: 'Cancelar' };

  protected override aoIniciar(): void {
    this.api.filiais().subscribe({
      next: (o: Opcao[]) => this.filiaisOptions = o,
      error: () => this.notif.warning('Não foi possível carregar a lista de filiais.')
    });
  }

  protected override decorar(regra: RegraNatureza): RegraNatureza {
    return { ...regra, vigencia: this.vigenciaDe(regra.dtIni, regra.dtFim) };
  }

  protected corpo(regra: RegraNatureza): Record<string, string> {
    const body: Record<string, string> = {
      filial: regra.filial ?? '',
      irrf: regra.irrf,
      pcc: regra.pcc,
      inss: regra.inss,
      iss: regra.iss,
      natureza: regra.natureza,
      tolerancia: String(regra.tolerancia ?? 0),
      ordem: regra.ordem,
      dtIni: regra.dtIni ?? '',
      dtFim: regra.dtFim ?? '',
      ativo: regra.ativo
    };
    if (regra.codigo) { body['codigo'] = regra.codigo; }
    return body;
  }

  abrirNova(): void {
    this.editing = null;
    this.duplicando = false;
    this.formGroup.reset(this.valoresPadrao());
    this.formModal.open();
  }

  editar(regra: RegraNatureza): void {
    this.editing = regra;
    this.duplicando = false;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra });
    this.formModal.open();
  }

  duplicar(regra: RegraNatureza): void {
    this.editing = null;
    this.duplicando = true;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra, codigo: '', ordem: '' });
    this.formModal.open();
  }

  salvar(): void {
    const regra = this.formGroup.getRawValue() as RegraNatureza;
    regra.ordem = this.normalizarOrdem(regra.ordem);

    const erro = this.validar(regra);
    if (erro) {
      this.notif.warning(erro);
      return;
    }

    this.gravar(regra, !!this.editing?.codigo, () => this.formModal.close());
  }

  private validar(f: RegraNatureza): string {
    if (!f.irrf) { return 'Informe a faixa de IRRF.'; }
    if (!f.pcc) { return 'Informe se há retenção de PIS/COFINS/CSLL.'; }
    if (!f.inss) { return 'Informe se há retenção de INSS.'; }
    if (!f.iss) { return 'Informe se há retenção de ISS.'; }
    if (!f.natureza) { return 'Informe a natureza financeira resultante.'; }
    if (f.tolerancia === null || f.tolerancia === undefined || Number(f.tolerancia) < 0) {
      return 'A tolerância não pode ser negativa.';
    }
    const ordem = String(f.ordem ?? '').trim();
    if (!ordem || !/^\d{1,3}$/.test(ordem) || Number(ordem) < 1) {
      return 'Informe a ordem de avaliação (1 a 999).';
    }
    if (f.dtIni && f.dtFim && f.dtFim < f.dtIni) {
      return 'O fim da vigência não pode ser anterior ao início.';
    }
    return '';
  }

  private valoresPadrao(): Partial<RegraNatureza> {
    return {
      codigo: '', filial: '', irrf: '', pcc: '', inss: '', iss: '', natureza: '',
      tolerancia: 0.10, ordem: '', dtIni: this.hojeIso(), dtFim: '', ativo: '1'
    };
  }
}
